import { Alert, Button, Checkbox, FieldBlock, Input, LoadingState, Select, Textarea, useToast } from '@orbind/ui'
import { TIPOS_DE_NOTIFICACAO, type PreferenciaDeNotificacao } from '@orbind/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { FormRow, FormScreen, FormSection } from '../components/FormScreen.tsx'
import { api, apiBase, apiOrigin, webTokenStorage } from '../lib/api.ts'
import { sessionAvatarSrc } from '../lib/session.tsx'
import { ChangePasswordModal } from '../features/auth/ChangePasswordModal.tsx'
import { useMeuConector } from '../features/ai-connector/useConectorDeIa.ts'
import './profile.css'

/**
 * Perfil do usuario (SPEC-20260814-2128), no padrao FormScreen (referencia
 * Conta Azul): secoes empilhadas recolhiveis, campos em colunas na linha e
 * barra de acoes no rodape com dirty check. A foto e enviada como imagem
 * (multipart, como o logo do workspace) e vale na hora; a exclusao da conta
 * fica na zona de perigo, recolhida por padrao.
 */

type Me = Awaited<ReturnType<Awaited<ReturnType<typeof api.auth.me.$get>>['json']>>['user']
type FormPerfil = Pick<Me, 'name' | 'avatarUrl' | 'localeId' | 'timezone'> & { bio: string }

/** Iniciais para o avatar sem foto: primeira e ultima palavra do nome. */
function iniciaisDe(nome: string) {
  const partes = nome.trim().split(/\s+/).filter(Boolean)
  if (partes.length === 0) return '?'
  const primeira = partes[0]![0] ?? ''
  const ultima = partes.length > 1 ? (partes[partes.length - 1]![0] ?? '') : ''
  return (primeira + ultima).toUpperCase()
}

export function ProfilePage() {
  const toast = useToast()
  const qc = useQueryClient()
  const navigate = useNavigate()
  // estado do conector do Claude (SPEC-20261103-1936): a pessoa procura aqui antes do menu
  const conector = useMeuConector()
  const fileRef = useRef<HTMLInputElement | null>(null)
  const bannerRef = useRef<HTMLInputElement | null>(null)

  const me = useQuery({
    queryKey: ['session'],
    queryFn: async () => (await api.auth.me.$get()).json(),
    staleTime: 5 * 60 * 1000,
  })
  const locales = useQuery({
    queryKey: ['locales'],
    queryFn: async () => (await api.i18n.locales.$get()).json(),
  })

  const [form, setForm] = useState<FormPerfil | null>(null)
  useEffect(() => {
    if (me.data?.user && form === null) {
      const u = me.data.user
      setForm({ name: u.name, avatarUrl: u.avatarUrl, localeId: u.localeId, timezone: u.timezone, bio: u.bio ?? '' })
    }
  }, [me.data, form])

  /**
   * O que chega por e-mail, por tipo de notificação (SPEC-20261103-1626).
   * Entra no mesmo Salvar/dirty da tela: mexer no perfil e nas notificações e
   * salvar uma vez grava os dois.
   */
  const preferencias = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const resposta = await api['notification-preferences'].$get()
      if (!resposta.ok) throw new Error('Não foi possível carregar as notificações por e-mail.')
      return (await resposta.json()).preferencias
    },
  })
  const [emailPorTipo, setEmailPorTipo] = useState<Record<string, boolean> | null>(null)
  useEffect(() => {
    if (preferencias.data && emailPorTipo === null) {
      setEmailPorTipo(Object.fromEntries(preferencias.data.map((p) => [p.kind, p.email])))
    }
  }, [preferencias.data, emailPorTipo])
  const preferenciasAlteradas =
    emailPorTipo !== null && (preferencias.data ?? []).some((p) => emailPorTipo[p.kind] !== p.email)

  const [trocandoSenha, setTrocandoSenha] = useState(false)

  const recarregarSessao = () => {
    void qc.invalidateQueries({ queryKey: ['session'] })
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
  }

  const salvar = useMutation({
    mutationFn: async (oQue: { perfil: boolean; notificacoes: boolean }) => {
      if (oQue.perfil) {
        const resposta = await api.auth.me.$patch({
          json: {
            name: form!.name.trim(),
            avatarUrl: form!.avatarUrl?.trim() || null,
            bio: form!.bio.trim(),
            localeId: form!.localeId,
            timezone: form!.timezone.trim() || 'America/Sao_Paulo',
          },
        })
        if (!resposta.ok) throw new Error('Não foi possível salvar o perfil.')
        recarregarSessao()
      }
      if (oQue.notificacoes && emailPorTipo) {
        const escolhas: PreferenciaDeNotificacao[] = TIPOS_DE_NOTIFICACAO.map((t) => ({
          kind: t.kind,
          email: emailPorTipo[t.kind] ?? true,
        }))
        const resposta = await api['notification-preferences'].$put({ json: { preferencias: escolhas } })
        if (!resposta.ok) throw new Error('Não foi possível salvar as notificações por e-mail.')
        qc.setQueryData(['notification-preferences'], (await resposta.json()).preferencias)
      }
    },
    onSuccess: () => {
      toast.push({ tone: 'success', message: 'Perfil atualizado.' })
    },
    onError: (erro) =>
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível salvar.' }),
  })

  // Foto do perfil: multipart, aplica na hora (como o logo do workspace).
  const enviarFoto = async (arquivo: File | null) => {
    if (!arquivo) return
    const corpo = new FormData()
    corpo.append('file', arquivo)
    const resposta = await fetch(`${apiBase()}/auth/me/avatar`, {
      method: 'POST',
      headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
      body: corpo,
    })
    if (!resposta.ok) {
      toast.push({ tone: 'danger', message: 'Não foi possível enviar a foto (imagem de até 1 MB).' })
      return
    }
    recarregarSessao()
    toast.push({ tone: 'success', message: 'Foto atualizada.' })
  }

  /** Banner do perfil: a faixa atrás da foto (2026-09-16). */
  const enviarBanner = async (arquivo: File | null) => {
    if (!arquivo) return
    const corpo = new FormData()
    corpo.append('file', arquivo)
    const resposta = await fetch(`${apiBase()}/auth/me/banner`, {
      method: 'POST',
      headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
      body: corpo,
    })
    if (!resposta.ok) {
      toast.push({ tone: 'danger', message: 'Não foi possível enviar o banner (imagem de até 2 MB).' })
      return
    }
    recarregarSessao()
    toast.push({ tone: 'success', message: 'Banner atualizado.' })
  }

  const removerBanner = async () => {
    const resposta = await api.auth.me.banner.$delete()
    if (!resposta.ok) {
      toast.push({ tone: 'danger', message: 'Não foi possível remover o banner.' })
      return
    }
    recarregarSessao()
    toast.push({ tone: 'success', message: 'Banner removido.' })
  }

  const removerFoto = async () => {
    const resposta = await api.auth.me.avatar.$delete()
    if (!resposta.ok) {
      toast.push({ tone: 'danger', message: 'Não foi possível remover a foto.' })
      return
    }
    recarregarSessao()
    toast.push({ tone: 'success', message: 'Foto removida.' })
  }

  // ------------------------------------------------- exclusao de conta ---

  const previa = useQuery({
    queryKey: ['account-deletion-preview'],
    queryFn: async () => (await api.auth.account['deletion-preview'].$get()).json(),
  })
  const [senhaExclusao, setSenhaExclusao] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [erroExclusao, setErroExclusao] = useState<string | null>(null)

  const excluirConta = useMutation({
    mutationFn: async () => {
      const resposta = await api.auth.account.$delete({ json: { password: senhaExclusao } })
      return resposta.json()
    },
    onSuccess: () => {
      // conta nao existe mais: sessao morre aqui, sem volta
      webTokenStorage.set(null)
      window.location.assign('/login')
    },
    onError: (erro) =>
      setErroExclusao(erro instanceof Error && erro.message ? erro.message : 'Não foi possível excluir a conta.'),
  })

  if (me.isPending || form === null) {
    return (
      <div className="profile">
        <LoadingState variant="detail" label="Carregando o perfil" />
      </div>
    )
  }
  if (me.isError) {
    return (
      <div className="profile">
        <Alert tone="danger" title="Não foi possível carregar o perfil">
          Recarregue a página ou entre novamente.
        </Alert>
      </div>
    )
  }

  const usuario = me.data.user
  const fotoSrc = sessionAvatarSrc(usuario) ?? (form.avatarUrl?.trim() || null)
  // A versão no `?v=` faz o navegador buscar de novo depois de trocar a imagem.
  const bannerSrc = usuario.hasBanner
    ? `${apiOrigin()}/public/member-banner/${usuario.id}?v=${encodeURIComponent(String(usuario.avatarVersion ?? ''))}`
    : null
  const iniciais = iniciaisDe(form.name)

  /** Dirty check: compara com o cadastro carregado, normalizado como o PATCH. */
  const persistiveis = (d: Pick<FormPerfil, 'name' | 'avatarUrl' | 'localeId' | 'timezone'> & { bio: string | null }) => ({
    name: d.name.trim(),
    avatarUrl: d.avatarUrl?.trim() || null,
    // No cadastro a apresentação vazia é `null`; no formulário é ''.
    bio: d.bio?.trim() || null,
    localeId: d.localeId,
    timezone: d.timezone.trim() || 'America/Sao_Paulo',
  })
  const perfilAlterado = JSON.stringify(persistiveis(form)) !== JSON.stringify(persistiveis(usuario))
  const alterado = perfilAlterado || preferenciasAlteradas
  const salvarValido = alterado && form.name.trim().length >= 2 && !salvar.isPending
  const salvarTudo = () => salvar.mutate({ perfil: perfilAlterado, notificacoes: preferenciasAlteradas })

  const donoDe = previa.data?.ownedWorkspaces ?? []
  const membroDe = previa.data?.memberOnlyWorkspaces ?? []
  const membrosAfetados = donoDe.reduce((soma, w) => soma + Math.max(0, w.memberCount - 1), 0)
  const podeExcluir = senhaExclusao.length > 0 && confirmacao.trim().toUpperCase() === 'EXCLUIR'

  return (
    <>
      <FormScreen
        title="Meu perfil"
        subtitle={usuario.email}
        dirty={alterado}
        saveLabel="Salvar"
        saveDisabled={!salvarValido}
        saving={salvar.isPending}
        onSubmit={() => salvarValido && salvarTudo()}
        footerExtra={
          <Button
            variant="ghost"
            disabled={!alterado}
            onClick={() => {
              setForm({
                name: usuario.name,
                avatarUrl: usuario.avatarUrl,
                localeId: usuario.localeId,
                timezone: usuario.timezone,
                bio: usuario.bio ?? '',
              })
              setEmailPorTipo(null)
            }}
          >
            Descartar
          </Button>
        }
      >
        {/* ---------------------------------------------- identidade */}
        <FormSection title="Informações pessoais">
          {/*
            Banner atrás da foto (pedido do usuário, 2026-09-16): a capa do
            perfil que os colegas veem no cartão de presença. Sem imagem, fica
            a faixa lisa do tema — a tela não muda de forma.
          */}
          <div className="profile__capa" data-ob-ampliar>
            {bannerSrc ? <img className="profile__capa-img" src={bannerSrc} alt="Banner do seu perfil" /> : null}
            <div className="profile__capa-acoes">
              <Button variant="secondary" icon="far fa-image" onClick={() => bannerRef.current?.click()}>
                {usuario.hasBanner ? 'Trocar banner' : 'Enviar banner'}
              </Button>
              {usuario.hasBanner ? (
                <Button variant="ghost" icon="far fa-trash-alt" onClick={() => void removerBanner()}>
                  Remover
                </Button>
              ) : null}
            </div>
            <input
              ref={bannerRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                void enviarBanner(e.target.files?.[0] ?? null)
                e.target.value = ''
              }}
            />
          </div>

          <div className="profile__photo-row">
            <span className="profile__photo" aria-hidden>
              {fotoSrc ? <img src={fotoSrc} alt="" /> : iniciais}
            </span>
            <div className="profile__photo-actions">
              <Button variant="secondary" icon="far fa-image" onClick={() => fileRef.current?.click()}>
                {usuario.hasAvatar ? 'Trocar foto' : 'Enviar foto'}
              </Button>
              {usuario.hasAvatar ? (
                <Button variant="ghost" icon="far fa-trash-alt" onClick={() => void removerFoto()}>
                  Remover
                </Button>
              ) : null}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                void enviarFoto(e.target.files?.[0] ?? null)
                e.target.value = ''
              }}
            />
          </div>

          <FormRow cols="1fr 1fr">
            <FieldBlock label="Nome completo" required>
              {({ id }) => (
                <Input id={id} value={form.name} onChange={(e) => setForm((f) => f && { ...f, name: e.target.value })} />
              )}
            </FieldBlock>
            <FieldBlock label="E-mail" hint="O e-mail é o identificador da conta e não pode ser alterado.">
              {({ id }) => <Input id={id} value={usuario.email} disabled />}
            </FieldBlock>
          </FormRow>

          <FormRow cols="minmax(220px, 620px)">
            <FieldBlock
              label="Sobre você"
              hint="Aparece no seu perfil para os colegas. Até 400 caracteres."
            >
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={3}
                  maxLength={400}
                  value={form.bio}
                  placeholder="Conte em poucas linhas o que você faz, seu horário, como preferem te chamar…"
                  onChange={(e) => setForm((f) => f && { ...f, bio: e.target.value })}
                />
              )}
            </FieldBlock>
          </FormRow>

          {!usuario.hasAvatar ? (
            <FormRow cols="minmax(220px, 520px)">
              <FieldBlock label="Endereço externo da foto" hint="Opcional — usado só enquanto você não envia uma imagem.">
                {({ id }) => (
                  <Input
                    id={id}
                    value={form.avatarUrl ?? ''}
                    placeholder="https://…"
                    onChange={(e) => setForm((f) => f && { ...f, avatarUrl: e.target.value || null })}
                  />
                )}
              </FieldBlock>
            </FormRow>
          ) : null}
        </FormSection>

        {/* -------------------------------------------- preferências */}
        <FormSection title="Preferências">
          <FormRow cols="1fr 1fr">
            <FieldBlock label="Idioma">
              {({ id }) => (
                <Select<string>
                  id={id}
                  value={form.localeId}
                  options={(locales.data?.locales ?? []).map((l) => ({ value: l.id, label: l.nativeName }))}
                  placeholder="Padrão da empresa"
                  onChange={(localeId) => setForm((f) => f && { ...f, localeId })}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Fuso horário" hint="No formato IANA — ex.: America/Sao_Paulo.">
              {({ id }) => (
                <Input id={id} value={form.timezone} onChange={(e) => setForm((f) => f && { ...f, timezone: e.target.value })} />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        {/* ------------------------------- notificações por e-mail */}
        <FormSection title="Notificações por e-mail">
          <p className="profile__danger-desc">
            Cada aviso do sino também chega em {usuario.email}. Desmarque o que não quer receber por e-mail — no sino
            continua aparecendo.
          </p>
          {preferencias.isPending ? (
            <LoadingState rows={2} label="Carregando as notificações por e-mail" />
          ) : preferencias.isError || emailPorTipo === null ? (
            <Alert
              tone="danger"
              title="Não foi possível carregar as notificações por e-mail"
              action={
                <Button size="sm" icon="fas fa-redo" onClick={() => void preferencias.refetch()}>
                  Tentar de novo
                </Button>
              }
            >
              O resto do perfil continua funcionando.
            </Alert>
          ) : (
            <div className="profile__notificacoes">
              {TIPOS_DE_NOTIFICACAO.map((tipo) => {
                const ligado = emailPorTipo[tipo.kind] ?? true
                return (
                  <div key={tipo.kind} className="profile__notificacao">
                    <Checkbox
                      label={tipo.rotulo}
                      checked={ligado}
                      onChange={(e) => setEmailPorTipo((m) => ({ ...(m ?? {}), [tipo.kind]: e.target.checked }))}
                    />
                    <span className="profile__notificacao-desc">
                      {tipo.descricao} {ligado ? 'Chega por e-mail e no sino.' : 'Só no sino.'}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </FormSection>

        {/* ----------------------------------------------- segurança */}
        <FormSection title="Segurança">
          <div>
            <Button variant="secondary" icon="fas fa-key" onClick={() => setTrocandoSenha(true)}>
              Alterar senha
            </Button>
          </div>
        </FormSection>

        {/* ------------------------------------------------ Claude
            Atalho (SPEC-20261103-1936): a conexão vive na tela própria, mas
            quem procura "conectar minha conta" procura aqui primeiro. */}
        <FormSection title="Claude">
          {conector.isPending ? (
            <p className="profile__dica">Conferindo sua liberação…</p>
          ) : conector.isError ? (
            <p className="profile__dica">Não foi possível conferir sua liberação agora.</p>
          ) : !conector.data.disponivel ? (
            <Alert tone="info" title="Conectores de IA não estão no plano deste workspace">
              Fale com o responsável pela conta para ampliar o plano.
            </Alert>
          ) : !conector.data.liberado ? (
            <Alert tone="warning" title="Ainda não habilitado para você">
              Peça a quem administra o workspace para liberar seu acesso em Administração › Conectores de IA. Depois
              disso, a conexão é feita na tela Conectar ao Claude.
            </Alert>
          ) : (
            <p className="profile__dica">
              <i className="fas fa-check-circle" aria-hidden /> Habilitado neste workspace
              {conector.data.tokens.length > 0
                ? ` · ${conector.data.tokens.length} conexão${conector.data.tokens.length === 1 ? '' : 'ões'} ativa${conector.data.tokens.length === 1 ? '' : 's'}`
                : ' · nenhuma conexão ainda'}
              . Tokens, pacote do Claude Desktop, endereço para o claude.ai e a skill ficam na tela Conectar ao Claude
              (também no menu do avatar).
            </p>
          )}
          <div>
            <Button variant="secondary" icon="fas fa-robot" onClick={() => navigate('/claude')}>
              Conectar ao Claude
            </Button>
          </div>
        </FormSection>

        {/* ------------------------------------------ zona de perigo */}
        <FormSection
          title={
            <>
              <i className="fas fa-triangle-exclamation" aria-hidden /> Excluir conta
            </>
          }
          tone="danger"
          defaultOpen={false}
        >
          {previa.isPending ? (
            <LoadingState variant="list" rows={2} label="Calculando o impacto" />
          ) : (
            <>
              <p className="profile__danger-desc">
                A exclusão é <strong>imediata e definitiva</strong>. Junto com a sua conta serão excluídos os
                workspaces em que você é <strong>dono</strong> — com tudo dentro (Itens de Trabalho, demandas,
                quadros, cadastros e membros):
              </p>
              {donoDe.length === 0 ? (
                <p className="profile__danger-desc">Você não é dono de nenhum workspace.</p>
              ) : (
                <ul className="profile__danger-list">
                  {donoDe.map((w) => (
                    <li key={w.id}>
                      <strong>{w.name}</strong> — {w.memberCount} {w.memberCount === 1 ? 'membro' : 'membros'},{' '}
                      {w.workItemCount} {w.workItemCount === 1 ? 'item' : 'itens'}
                    </li>
                  ))}
                </ul>
              )}
              {membrosAfetados > 0 ? (
                <Alert tone="warning" title={`${membrosAfetados} outra(s) pessoa(s) perderão acesso`}>
                  Há outros membros nos workspaces acima — eles perdem tudo que está lá.
                </Alert>
              ) : null}
              {membroDe.length > 0 ? (
                <p className="profile__danger-desc">
                  Nos workspaces em que você é só membro ({membroDe.map((w) => w.name).join(', ')}), apenas o seu
                  vínculo é removido — os dados deles ficam.
                </p>
              ) : null}

              {erroExclusao ? (
                <Alert tone="danger" title="Não foi possível excluir">
                  {erroExclusao}
                </Alert>
              ) : null}

              <FormRow cols="1fr 1fr">
                <FieldBlock label="Sua senha" required>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="password"
                      autoComplete="current-password"
                      value={senhaExclusao}
                      onChange={(e) => setSenhaExclusao(e.target.value)}
                    />
                  )}
                </FieldBlock>
                <FieldBlock label="Digite EXCLUIR para confirmar" required>
                  {({ id }) => (
                    <Input id={id} value={confirmacao} placeholder="EXCLUIR" onChange={(e) => setConfirmacao(e.target.value)} />
                  )}
                </FieldBlock>
              </FormRow>
              <div>
                <Button
                  variant="danger"
                  icon="fas fa-trash-alt"
                  loading={excluirConta.isPending}
                  disabled={!podeExcluir}
                  onClick={() => {
                    setErroExclusao(null)
                    excluirConta.mutate()
                  }}
                >
                  Excluir minha conta definitivamente
                </Button>
              </div>
            </>
          )}
        </FormSection>
      </FormScreen>

      <ChangePasswordModal open={trocandoSenha} onClose={() => setTrocandoSenha(false)} />
    </>
  )
}
